import { useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment";
import { toast } from "react-toastify";
import { MdContentCopy, MdRefresh, MdWarningAmber } from "react-icons/md";
import { useNavigate } from "react-router-dom";

import axios from "../axiosConfig";
import { BASE_SERVER_URL } from "../sampleData";
import "../inventory.css";

/* ------------------------------------------------------------------ *
 * Flower inventory (redesign) — /inventory
 *
 * Replaces src/pages/inventoryList.js (kept at /InventoryList-v1). Same
 * single endpoint: POST /inventory/getAllByDate { date: "DD/MM/YYYY" },
 * which answers with `response` as a { flower name: stem count } map and
 * `notFound` when some ordered product had no flower content registered.
 *
 * What changes:
 *   - the day could only be picked from a calendar       -> ‹ › stepper +
 *                                                          Today, since
 *                                                          this list is
 *                                                          read day by day
 *   - no total anywhere: you added the column up by hand -> stems,
 *                                                          distinct
 *                                                          flowers and the
 *                                                          biggest line
 *   - rows came out in whatever order the map enumerated -> sortable, by
 *                                                          quantity first
 *   - no way to find one flower in a long list           -> search
 *   - the caution about missing flower contents was a
 *     red sentence that didn't say what to do            -> a notice that
 *                                                          links to the
 *                                                          products page
 *   - `flowers` was initialised to {} but typed as a list,
 *     and a failed request left the spinner up for ever  -> both fixed
 *   - dead state (orderType, page, sortBy, query, allPage,
 *     selectedRun) and an unused `filter` object built
 *     on every fetch                                     -> removed
 * ------------------------------------------------------------------ */

const DATE_FMT = "DD/MM/YYYY";
const today = () => moment().format(DATE_FMT);

export function InventoryRedesign() {
  const navigate = useNavigate();

  const [date, setDate] = useState(today);
  const [flowers, setFlowers] = useState({});
  const [missingContent, setMissingContent] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState({ key: "qty", dir: -1 });

  const refresh = useCallback(() => {
    setLoading(true);
    setLoadError(false);
    axios
      .post(BASE_SERVER_URL + "/inventory/getAllByDate", { date })
      .then((doc) => {
        if (doc.data.status !== "success") {
          setLoadError(true);
          setLoading(false);
          return;
        }
        setFlowers(doc.data.response || {});
        setMissingContent(Boolean(doc.data.notFound));
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        setLoadError(true);
      });
  }, [date]);

  useEffect(refresh, [refresh]);

  /* The endpoint answers with a { name: amount } map, so the list, the
     totals and the sort all derive from one pass over its entries. */
  const rows = useMemo(
    () =>
      Object.keys(flowers || {}).map((name) => ({
        name,
        qty: Number(flowers[name]) || 0,
      })),
    [flowers]
  );

  const totalStems = useMemo(
    () => rows.reduce((sum, r) => sum + r.qty, 0),
    [rows]
  );
  const biggest = useMemo(
    () => rows.reduce((max, r) => (r.qty > (max ? max.qty : -1) ? r : max), null),
    [rows]
  );

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = q
      ? rows.filter((r) => r.name.toLowerCase().indexOf(q) !== -1)
      : rows;
    const sorted = [...filtered].sort((a, b) =>
      sort.key === "name"
        ? a.name.localeCompare(b.name) * sort.dir
        : (a.qty - b.qty) * sort.dir || a.name.localeCompare(b.name)
    );
    return sorted;
  }, [rows, search, sort]);

  const shownStems = useMemo(
    () => shown.reduce((sum, r) => sum + r.qty, 0),
    [shown]
  );

  function stepDay(days) {
    setDate(moment(date, DATE_FMT).add(days, "day").format(DATE_FMT));
  }

  function toggleSort(key) {
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir * -1 }
        : { key, dir: key === "name" ? 1 : -1 }
    );
  }

  /* The point of this screen is ordering stems from a supplier, so the
     list needs to leave the screen as text. */
  function copyList() {
    const text = shown.map((r) => `${r.qty}\t${r.name}`).join("\n");
    if (!text) {
      toast.info("Nothing to copy.");
      return;
    }
    const payload = `Flower inventory — ${date}\n${text}\nTotal\t${shownStems}`;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(payload)
        .then(() => toast.success("Flower list copied."))
        .catch(() => toast.error("Couldn't copy that list."));
    } else {
      toast.error("Copying isn't available in this browser.");
    }
  }

  const isToday = date === today();
  const day = moment(date, DATE_FMT);
  const arrow = sort.dir === 1 ? "▲" : "▼";

  return (
    <div className="iv-page">
      {/* ---------------- toolbar ---------------- */}
      <div className="iv-bar">
        <div className="iv-day">
          <button
            type="button"
            className="iv-step"
            onClick={() => stepDay(-1)}
            title="Previous day"
          >
            ‹
          </button>
          <input
            type="date"
            value={day.isValid() ? day.format("YYYY-MM-DD") : ""}
            onChange={(e) =>
              e.target.value &&
              setDate(moment(e.target.value, "YYYY-MM-DD").format(DATE_FMT))
            }
          />
          <button
            type="button"
            className="iv-step"
            onClick={() => stepDay(1)}
            title="Next day"
          >
            ›
          </button>
        </div>
        <button
          type="button"
          className="iv-today"
          onClick={() => setDate(today())}
          disabled={isToday}
        >
          Today
        </button>

        <label className="iv-search">
          <span className="iv-search-icon" />
          <input
            type="search"
            value={search}
            placeholder="Find a flower"
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>

        <span className="rf-spacer" />

        <button
          type="button"
          className="rf-btn rf-btn-ghost"
          onClick={copyList}
          title="Copy the list as text"
        >
          <MdContentCopy style={{ verticalAlign: "-2px" }} /> Copy list
        </button>
        <button
          type="button"
          className="rf-btn rf-btn-ghost"
          onClick={refresh}
          title="Reload"
        >
          <MdRefresh style={{ verticalAlign: "-2px" }} /> Reload
        </button>
      </div>

      {/* ---------------- body ---------------- */}
      <div className="iv-body">
        <div className="iv-headline">
          <div className="iv-headline-main">
            <div className="iv-eyebrow">Flowers needed for</div>
            <h1 className="iv-title">
              {day.isValid() ? day.format("dddd D MMMM YYYY") : date}
            </h1>
            <p className="iv-title-rel">
              {isToday
                ? "Today"
                : day.isValid()
                ? day.from(moment().startOf("day"))
                : ""}
              {" · rolled up from every ordered product on this date"}
            </p>
          </div>

          <span className="rf-spacer" />

          <div className="iv-kpis">
            <div className="iv-kpi">
              <div className="iv-kpi-k">Stems</div>
              <div className="iv-kpi-v">{loading ? "—" : totalStems}</div>
            </div>
            <div className="iv-kpi">
              <div className="iv-kpi-k">Flower types</div>
              <div className="iv-kpi-v">{loading ? "—" : rows.length}</div>
            </div>
            <div className="iv-kpi">
              <div className="iv-kpi-k">Biggest line</div>
              <div className="iv-kpi-v">
                {loading || !biggest ? "—" : biggest.qty}
                {!loading && biggest && <small>stems</small>}
              </div>
              {!loading && biggest && (
                <div className="iv-kpi-sub">{biggest.name}</div>
              )}
            </div>
          </div>
        </div>

        {/* the legacy caution, restyled and made actionable */}
        {!loading && !loadError && missingContent && (
          <div className="iv-warn">
            <MdWarningAmber className="iv-warn-ic" />
            <div className="iv-warn-t">
              <b>These numbers are incomplete.</b> Some products ordered for this
              date have no flower contents registered against them, so their
              stems are missing from the list below. Register the contents on the{" "}
              <button
                type="button"
                className="iv-warn-link"
                onClick={() => navigate("/products")}
              >
                products page
              </button>{" "}
              and reload.
            </div>
          </div>
        )}

        {/* ---------------- table ---------------- */}
        <div className="iv-tablewrap">
          <div className="iv-scroll">
            <table className="iv-table">
              <thead>
                <tr>
                  <th className="iv-c-num">#</th>
                  <th>
                    <button
                      type="button"
                      className={`iv-sort${sort.key === "name" ? "" : " is-off"}`}
                      onClick={() => toggleSort("name")}
                    >
                      Flower <span className="iv-sort-ar">{arrow}</span>
                    </button>
                  </th>
                  <th className="iv-c-qty" style={{ textAlign: "right" }}>
                    <button
                      type="button"
                      className={`iv-sort${sort.key === "qty" ? "" : " is-off"}`}
                      onClick={() => toggleSort("qty")}
                    >
                      Stems <span className="iv-sort-ar">{arrow}</span>
                    </button>
                  </th>
                  <th className="iv-c-share">Share of the day</th>
                </tr>
              </thead>
              <tbody>
                {loading &&
                  Array.from({ length: 8 }).map((_, r) => (
                    <tr key={r}>
                      <td className="iv-c-num">
                        <div className="iv-skel" />
                      </td>
                      <td>
                        <div className="iv-skel" style={{ width: "45%" }} />
                      </td>
                      <td className="iv-c-qty">
                        <div className="iv-skel" />
                      </td>
                      <td className="iv-c-share">
                        <div className="iv-skel" />
                      </td>
                    </tr>
                  ))}

                {!loading && loadError && (
                  <tr>
                    <td colSpan={4}>
                      <div className="iv-state">
                        <b>Couldn&apos;t load the inventory.</b>
                        The server didn&apos;t answer that request.
                        <div>
                          <button
                            type="button"
                            className="rf-btn rf-btn-ghost"
                            onClick={refresh}
                          >
                            Try again
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}

                {!loading && !loadError && shown.length === 0 && (
                  <tr>
                    <td colSpan={4}>
                      <div className="iv-state">
                        <b>
                          {rows.length === 0
                            ? "Nothing ordered for this date."
                            : `No flower matches “${search.trim()}”.`}
                        </b>
                        {rows.length === 0
                          ? "Once products are ordered for this day, the flowers they need are totalled here."
                          : "Clear the search to see the whole list."}
                      </div>
                    </td>
                  </tr>
                )}

                {!loading &&
                  !loadError &&
                  shown.map((row, i) => {
                    const pct = totalStems ? (row.qty / totalStems) * 100 : 0;
                    return (
                      <tr
                        className={`iv-row${
                          biggest && row.name === biggest.name ? " is-top" : ""
                        }`}
                        key={row.name}
                      >
                        <td className="iv-c-num">{i + 1}</td>
                        <td className="iv-c-name">{row.name}</td>
                        <td className="iv-c-qty">{row.qty}</td>
                        <td className="iv-c-share">
                          <div className="iv-track">
                            <span style={{ width: `${pct}%` }} />
                          </div>
                          <span className="iv-pct">{pct.toFixed(1)}%</span>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>

          {!loading && !loadError && rows.length > 0 && (
            <div className="iv-foot">
              <span>
                {shown.length === rows.length
                  ? `${rows.length} flower${rows.length === 1 ? "" : "s"}`
                  : `${shown.length} of ${rows.length} flowers`}
              </span>
              <span className="rf-spacer" />
              <span>
                {shownStems === totalStems
                  ? `${totalStems} stems in total`
                  : `${shownStems} stems shown · ${totalStems} in total`}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default InventoryRedesign;
